import { useMemo } from 'react';

import { type Query, useQueries } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type {
	PostTypeAssetStatus,
	PostTypeData,
	PostTypeDataMap,
	ReadyToSharePlatformPostType,
} from 'src/app/pages/starterDashboardV2/shared/types';
import { getPetitionReadyToSharePostsV2, UnrecoverableError } from 'src/app/shared/api/readyToShare';

export type { PostTypeAssetStatus, PostTypeData, PostTypeDataMap, ReadyToSharePlatformPostType };

type UseFetchReadyToSharePostsByTypeProps = {
	slug: string;
	postTypes: readonly ReadyToSharePlatformPostType[];
	shouldExecute: boolean;
	pollingInterval?: number;
};

const POLLING_INTERVAL = 3000; // 3 seconds
const MAX_RETRIES = 5;

type CreateQueryParams = {
	postType: ReadyToSharePlatformPostType;
	slug: string;
	utilityContext: ReturnType<typeof useUtilityContext>;
	shouldExecute: boolean;
	pollingInterval: number;
};

async function fetchPostTypeData(
	slug: string,
	postType: ReadyToSharePlatformPostType,
	utilityContext: ReturnType<typeof useUtilityContext>,
): Promise<PostTypeData> {
	const posts = await getPetitionReadyToSharePostsV2({ slugOrId: slug, type: postType }, utilityContext);

	return {
		posts,
		status: posts.length > 0 ? 'COMPLETE' : 'NOT_AVAILABLE',
		postsCount: posts.length,
		errorMessage: null,
	};
}

function createPostTypeQuery({ postType, slug, utilityContext, shouldExecute, pollingInterval }: CreateQueryParams) {
	return {
		queryKey: ['readyToSharePostsV2', slug, postType] as const,
		queryFn: async () => fetchPostTypeData(slug, postType, utilityContext),
		enabled: shouldExecute,
		retry: (failureCount: number, error: Error) => {
			if (error instanceof UnrecoverableError) return false;
			return failureCount < MAX_RETRIES;
		},
		retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchInterval: (query: Query<PostTypeData, Error>) => {
			const { data, status, error } = query.state;
			// Stop polling when query is in a permanent error state (retries exhausted)
			if (status === 'error' || error) return false;
			// Stop polling when we have a terminal state (data available, or failed)
			// Continue polling if data is undefined (still loading/retrying)
			if (data && ['COMPLETE', 'FAILED'].includes(data.status)) {
				return false;
			}
			return pollingInterval;
		},
		staleTime: 0,
	};
}

function calculatePostTypeDataFromQueries(
	queriesData: Array<PostTypeData | undefined>,
	queriesState: Array<{ isLoading: boolean; isFetching: boolean; isError: boolean }>,
	postTypes: readonly ReadyToSharePlatformPostType[],
): PostTypeDataMap {
	const data: PostTypeDataMap = {};
	queriesData.forEach((queryData, index) => {
		const postType = postTypes[index];
		const state = queriesState[index];

		if (state.isLoading || state.isFetching) {
			data[postType] = {
				status: 'PENDING',
				posts: [],
				postsCount: 0,
				errorMessage: null,
			};
		} else if (state.isError) {
			data[postType] = {
				status: 'FAILED',
				posts: [],
				postsCount: 0,
				errorMessage: null,
			};
		} else if (queryData) {
			data[postType] = queryData;
		}
	});
	return data;
}

export function useFetchReadyToSharePostsByType({
	slug,
	postTypes,
	shouldExecute,
	pollingInterval = POLLING_INTERVAL,
}: UseFetchReadyToSharePostsByTypeProps) {
	const utilityContext = useUtilityContext();

	const queries = useQueries({
		queries: postTypes.map((postType) =>
			createPostTypeQuery({ postType, slug, utilityContext, shouldExecute, pollingInterval }),
		),
	});

	// Extract data from queries to avoid unstable dependencies
	const queriesData = queries.map((q) => q.data);
	const queriesState = queries.map((q) => ({ isLoading: q.isLoading, isFetching: q.isFetching, isError: q.isError }));

	const postTypeData = useMemo(
		() => calculatePostTypeDataFromQueries(queriesData, queriesState, postTypes),
		[queriesData, queriesState, postTypes],
	);

	const { allComplete, anyFailed, anyPending, hasAnyData, allDone } = useMemo(() => {
		const statuses = Object.values(postTypeData).filter((d): d is PostTypeData => d !== undefined);
		return {
			allComplete: statuses.length > 0 && statuses.every((d) => d.status === 'COMPLETE'),
			anyFailed: statuses.some((d) => d.status === 'FAILED'),
			anyPending: statuses.some((d) => d.status === 'PENDING'),
			hasAnyData: statuses.some((d) => d.status === 'COMPLETE' && d.postsCount > 0),
			allDone: statuses.length === postTypes.length && statuses.every((d) => ['COMPLETE', 'FAILED'].includes(d.status)),
		};
	}, [postTypeData, postTypes]);

	// Show loading only if ALL queries are still loading AND none have data yet
	// Once any query returns data, hide the loading state
	const isLoading = queries.every((q) => q.isLoading) && !hasAnyData;
	const allFailed = queries.length > 0 && queries.every((q) => q.isError);

	return {
		postTypeData,
		isLoading,
		allComplete,
		anyFailed,
		anyPending,
		hasAnyData,
		allDone,
		allFailed,
		queries,
	};
}

export type UseFetchReadyToSharePostsByTypeReturn = ReturnType<typeof useFetchReadyToSharePostsByType>;
